import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCcw } from "lucide-react";
import { fileUrl } from "@/lib/api";

export function collectibleFromWalk(walk) {
  if (!walk || walk.collectible_enabled === false) return null;
  const order = String(walk.order || "").trim();
  return {
    walk_id: walk.id,
    walk_slug: walk.slug,
    format: walk.collectible_format || "A6",
    physical_available: Boolean(walk.collectible_physical_enabled),
    number: String(walk.collectible_number || (order ? order.padStart(2, "0") : "")).trim(),
    title: walk.collectible_title || walk.title || "SPUREN",
    subtitle: walk.collectible_subtitle || walk.subtitle,
    description: walk.collectible_description || walk.short_description,
    front_image: walk.collectible_front_image,
    back_image: walk.collectible_back_image,
    fallback_image: walk.hero_image,
    cognitive_url: walk.collectible_cognitive_url,
    creator_label: walk.collectible_creator_label,
    creator_url: walk.collectible_creator_url,
  };
}

function DesignedSide({ src, alt }) {
  return <img src={fileUrl(src)} alt={alt} className="h-full w-full object-contain" />;
}

const FORMAT_STYLES = {
  A6: { aspect: "aspect-[105/148]", compact: "max-w-[230px]", full: "max-w-[310px]" },
  TRADING_CARD: { aspect: "aspect-[63/88]", compact: "max-w-[220px]", full: "max-w-[300px]" },
  BOOKMARK: { aspect: "aspect-[7/20]", compact: "max-w-[155px]", full: "max-w-[220px]" },
};

function GeneratedFront({ card }) {
  return (
    <div className="flex h-full flex-col bg-[#f4efe3] text-[#123f35]">
      <div className="flex items-start justify-between px-5 pb-3 pt-5">
        <div>
          <p className="font-serif text-2xl font-semibold tracking-[0.16em]">SPUREN</p>
          <p className="text-[8px] font-bold tracking-[0.2em]">ORTE. MENSCHEN. GESCHICHTEN.</p>
        </div>
        {card.number ? (
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#123f35] font-serif text-xl text-white">
            {card.number}
          </span>
        ) : null}
      </div>
      <div className="px-5 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em]">Deine Spur</p>
        <h3 className="mt-1 font-serif text-3xl leading-none">{card.title}</h3>
        {card.subtitle ? <p className="mt-2 text-sm leading-snug text-[#365c52]">{card.subtitle}</p> : null}
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden bg-[#dfe7df]">
        {card.fallback_image ? (
          <img src={fileUrl(card.fallback_image)} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-6xl opacity-20">✦</div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#123f35]/90 to-transparent px-5 pb-5 pt-16 text-white">
          <p className="font-serif text-xl italic">Mit anderen Augen.</p>
        </div>
      </div>
    </div>
  );
}

function GeneratedBack({ card }) {
  return (
    <div className="flex h-full flex-col bg-[#f4efe3] p-6 text-[#123f35]">
      <p className="font-serif text-2xl italic leading-tight">„Manchmal genügt ein kleiner Schritt, um einen Ort mit anderen Augen zu sehen.“</p>
      <div className="my-5 h-px bg-[#123f35]/30" />
      <p className="text-sm leading-relaxed">
        {card.description || "Du bist diese Runde gegangen, hast Orte entdeckt und eine neue Spur gesammelt."}
      </p>
      <div className="my-auto flex items-center justify-center py-8">
        <div className="flex h-28 w-28 items-center justify-center rounded-full border border-dashed border-[#123f35]/30 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.16em]">Meine Spur{card.number ? ` · ${card.number}` : ""}</span>
        </div>
      </div>
      <div className="border-t border-[#123f35]/30 pt-4">
        <p className="font-serif text-xl font-semibold tracking-[0.12em]">SPUREN</p>
        <p className="text-[9px] font-bold tracking-[0.18em]">ORTE. MENSCHEN. GESCHICHTEN.</p>
      </div>
    </div>
  );
}

export default function CollectibleCard({ value, compact = false, testId = "collectible-card" }) {
  const card = value?.card || value;
  const [side, setSide] = useState("front");
  if (!card) return null;

  const format = FORMAT_STYLES[card.format] || FORMAT_STYLES.A6;
  const routeTarget = `/wege/${card.walk_slug || card.walk_id}`;
  const designedImage = side === "front" ? card.front_image : card.back_image;
  return (
    <div className={`mx-auto w-full ${compact ? format.compact : format.full}`} data-testid={testId}>
      <div className={`${format.aspect} overflow-hidden rounded-[1.4rem] border border-black/10 bg-white shadow-lg`}>
        {designedImage ? (
          <DesignedSide src={designedImage} alt={`${card.title || "SPUREN-Karte"} · ${side === "front" ? "Vorderseite" : "Rückseite"}`} />
        ) : side === "front" ? (
          <GeneratedFront card={card} />
        ) : (
          <GeneratedBack card={card} />
        )}
      </div>
      <button
        type="button"
        className="mx-auto mt-3 flex items-center gap-2 text-xs font-semibold text-[var(--brand)] underline underline-offset-4"
        onClick={() => setSide(side === "front" ? "back" : "front")}
        data-testid={`${testId}-flip`}
      >
        <RefreshCcw className="h-3.5 w-3.5" /> {side === "front" ? "RÜCKSEITE ANSEHEN" : "VORDERSEITE ANSEHEN"}
      </button>
      <p className="mt-3 text-center text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--ink-soft)]">
        {card.physical_available ? "Digital + als echte Karte" : "Digitale Sammelkarte"}
      </p>
      <div className="mt-3 flex flex-wrap justify-center gap-2 text-[10px] font-bold uppercase tracking-[0.08em]">
        {card.walk_id ? <Link to={routeTarget} className="rounded-full border border-[var(--brand)]/20 px-3 py-2 text-[var(--brand)]">SPUR ÖFFNEN</Link> : null}
        {card.cognitive_url ? (
          <a href={card.cognitive_url} target="_blank" rel="noreferrer" className="rounded-full border border-[var(--brand)]/20 px-3 py-2 text-[var(--brand)]">
            GEDANKENRAUM
          </a>
        ) : null}
      </div>
      {card.creator_label ? (
        card.creator_url ? (
          <a href={card.creator_url} target="_blank" rel="noreferrer" className="mt-3 block text-center text-[10px] text-[var(--ink-soft)] underline underline-offset-2">
            {card.creator_label}
          </a>
        ) : (
          <p className="mt-3 text-center text-[10px] text-[var(--ink-soft)]">{card.creator_label}</p>
        )
      ) : null}
    </div>
  );
}
